import axios from 'axios';
import { emptyBase64Image } from '../components/utils';
import defaultSelfie from '../assets/default-selfie.png';

import { mainAPIUrl } from '../../../settings';

const baseURL = `${mainAPIUrl}/gservices_3`;


export const getBase64FromUrl = async (url) => {
  const data = await fetch(url);
  if (!data.ok) {
    throw new Error(`Failed to fetch image: ${data.status} ${data.statusText}`);
  }
  const blob = await data.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      const base64data = reader.result;
      resolve(base64data);
    };
    reader.onerror = () => {
      reject(new Error('Failed to convert image to base64'));
    };
  });
};

export const fetchEditMessageAPICall = async (tokenId) => {
  const url = `${baseURL}/messages/${tokenId}`;

  const res = await axios.get(url);
  const currentMessage = res.data.data;

  if (currentMessage) {
    currentMessage.selfie = currentMessage.selfie
      ? await getBase64FromUrl(currentMessage.selfie)
      : defaultSelfie;
    currentMessage.doodle = currentMessage.doodle
      ? await getBase64FromUrl(currentMessage.doodle)
      : emptyBase64Image;
  }

  return currentMessage;
};

export const createMessageAPICall = async (messageData, occasionId) => {
  // Handle AI-generated images: if image_url and response_id are present,
  // set selfieBase64 to null and send image_url and response_id instead
  if (messageData.image_url && messageData.response_id) {
    messageData.selfieBase64 = null;
    // image_url and response_id are already in messageData, no conversion needed
  } else {
    // Non-AI image: process selfieBase64 as usual
    messageData.selfieBase64 =
      messageData.selfieBase64 === defaultSelfie
        ? null
        : messageData.selfieBase64.split(',')[1];
    // Remove AI metadata if not present (clean up - delete properties instead of setting to null)
    delete messageData.image_url;
    delete messageData.response_id;
  }

  messageData.drawingBase64 =
    messageData.drawingBase64 === emptyBase64Image
      ? null
      : messageData.drawingBase64.split(',')[1];

  if (!occasionId || occasionId === 'null' || occasionId === 'undefined') {
    try {
      const res = await axios.post(`${baseURL}/messages`, messageData);
      return res.data.data.messageId;
    } catch (err) {
      return null;
    }
  } else {
    try {
      const res = await axios.post(
        `${baseURL}/messages?occasionId=${occasionId}`,
        messageData
      );
      return res.data.data.messageId;
    } catch (err) {
      return null;
    }
  }
};

export const fetchOccasionMessagesAPICall = async (occasionId) => {
  const url = `${baseURL}/occasions/messages/${occasionId}`;

  try {
    const res = await axios.get(url);
    return res.data.messages;
  } catch (err) {
    return null;
  }
};

export const searchImageAPICall = async (query) => {
  const url = `${baseURL}/messages/selfie/images?query=${query}`;
  try {
    const res = await axios.get(url);
    return res.data;
  } catch (err) {
    return null;
  }
};

export const getPostCardDesigns = async (tokenId) => {

  let url = `${baseURL}/messages/designs`

  if (!tokenId || tokenId === 'null') {
    url += '/null';
  }
  else if (tokenId === 'undefined') {
    url += '/undefined';
  }
  else {
    url += `/${tokenId}`;
  }

  let availableDesigns = null;

  try {
    const res = await axios.get(url);
    availableDesigns = {
      postCardDesignSections: res.data.data.postCardDesignSections,
      defaultDesign: res.data.data.defaultDesign
    }
  }
  catch (err) {
    //return null;
  }

  if (!availableDesigns || !availableDesigns.postCardDesignSections) {
    return null;
  }

  return availableDesigns;
}

export const POSTTrackDownload = async (download_location) => {
  const url = `${baseURL}/messages/selfie/images/trackDownload`;
  const data = await fetch(url, {
    method: "POST",
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(download_location)
  });
  const json = await data.json();
  return json;
}

export const generateImageAPICall = async (tokenId, message, messageId) => {
  const url = `${baseURL}/messages/generate-image`;
  const headers = {
    'Authorization': `Bearer ${tokenId}`,
    'Content-Type': 'application/json'
  };
  const body = { message };
  if (messageId && messageId !== 'null' && messageId !== 'undefined') {
    body.message_id = messageId;
  }

  try {
    const res = await axios.post(url, body, { headers });

    // API returns: { status, code, data: { image_url, response_id } } or { code, message }
    // Normalize TOKEN_EXPIRED so callers can trigger a refresh flow without
    // surfacing low-level backend messages to the user.
    if (res.data && res.data.code) {
      // If backend signals token expiration via dedicated code, return a fixed, user-friendly message
      if (res.data.code === 'TOKEN_EXPIRED') {
        return {
          code: 'TOKEN_EXPIRED',
          message: 'Your session has expired. Please try again.',
        };
      }

      // Standard SUCCESS / ERROR responses are forwarded as-is
      if (res.data.code === 'SUCCESS' || res.data.code === 'ERROR') {
        return res.data;
      }
    }
    // If response doesn't match expected structure, treat as error
    return {
      code: 'ERROR',
      message: res.data?.message || res.data?.data?.message || 'Unexpected response format from server.'
    };
  } catch (err) {
    // Handle axios errors
    if (err.response && err.response.data) {
      // API returned an error response (e.g., 429, 400, 500)
      const errorData = err.response.data;
      // If the backend signals token expiration here, normalize it with a fixed message
      if (errorData.code === 'TOKEN_EXPIRED') {
        return {
          code: 'TOKEN_EXPIRED',
          message: 'Your session has expired. Please try again.',
        };
      }
      // Check if error response has expected structure
      if (errorData.code === 'ERROR' || errorData.code === 'SUCCESS') {
        return errorData;
      }
      // If error response doesn't match expected structure, format it
      return {
        code: 'ERROR',
        message: errorData.message || errorData.error || `HTTP ${err.response.status}: ${err.response.statusText}`
      };
    }
    // Network error or other axios error
    return {
      code: 'ERROR',
      message: err.message || 'Network error. Please check your connection and try again.'
    };
  }
}